import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Truck, X } from "lucide-react";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/auth-store";

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Čeka izdavanje",
  PARTIALLY_ISSUED: "Delimično izdato",
  COMPLETED: "Izvršeno",
  CANCELLED: "Otkazano",
};
const STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  PENDING: "neutral",
  PARTIALLY_ISSUED: "warning",
  COMPLETED: "success",
  CANCELLED: "neutral",
};


/**
 * "Pošalji kuriru" - u nalogu magacinu, posle unosa serijskih brojeva
 * (04.10.2026, korisnikov zahtev). Prikazuje se tek kad je nešto izdato
 * (status != PENDING). Samo kuriri sa implemented+enabled (trenutno samo
 * BEX, ako je podešen) se mogu izabrati - ostali su vidljivi kao "uskoro"
 * (vidi CourierSettingsTab).
 */
function CourierDispatchSection({ orderId, orderStatus }: { orderId: string; orderStatus: string }) {
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.token);

  const { data: couriers } = useQuery({
    queryKey: ["couriers"],
    queryFn: () => api.listCouriers(),
  });
  const { data: shipments } = useQuery({
    queryKey: ["shipments", orderId],
    queryFn: () => api.listShipmentsForWarehouseOrder(orderId),
  });

  const [courier, setCourier] = useState("");
  const [weightKg, setWeightKg] = useState("1");
  const [codAmount, setCodAmount] = useState("");
  const [labelFormat, setLabelFormat] = useState<"THERMAL" | "A4">("THERMAL");
  const [labelPosition, setLabelPosition] = useState("1");
  const [printError, setPrintError] = useState<string | null>(null);

  const activeShipment = shipments?.find((s) => s.status === "CREATED");
  const usableCouriers = (couriers ?? []).filter((c) => c.implemented && c.enabled);
  // Ako je podešen samo jedan kurir, izabran je automatski.
  const selectedCourier = courier || (usableCouriers.length === 1 ? (usableCouriers[0]?.code ?? "") : "");

  const dispatch = useMutation({
    mutationFn: () =>
      api.dispatchShipment(orderId, selectedCourier, {
        weightKg: weightKg ? Number(weightKg) : undefined,
        codAmount: codAmount ? Number(codAmount) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shipments", orderId] });
    },
  });

  const printLabel = async () => {
    if (!activeShipment) return;
    setPrintError(null);
    try {
      const url = api.getShipmentLabelUrl(activeShipment.id, labelFormat, Number(labelPosition) || 1);
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Preuzimanje nalepnice nije uspelo.");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (err) {
      setPrintError(err instanceof Error ? err.message : "Preuzimanje nalepnice nije uspelo.");
    }
  };

  if (orderStatus === "PENDING") return null;

  return (
    <div className="rounded-lg border border-border bg-slate-50 p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Truck className="h-4 w-4" aria-hidden /> Kurirska služba
      </h3>

      {activeShipment ? (
        <div className="mt-3 space-y-3 text-sm">
          <p>
            Poslato preko <strong>{activeShipment.courierLabel}</strong> - broj za praćenje:{" "}
            <strong>{activeShipment.trackingNumber}</strong>
          </p>
          {activeShipment.canRefresh && (
            <p className="text-xs text-slate-600">
              Status isporuke: <strong>{activeShipment.deliveryStateLabel ?? "čeka prvi status"}</strong>
              {activeShipment.deliveryStatusText ? ` (${activeShipment.deliveryStatusText})` : ""} - detalji u kartici "Praćenje pošiljki".
            </p>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-500">Format nalepnice</label>
              <select
                value={labelFormat}
                onChange={(e) => setLabelFormat(e.target.value as "THERMAL" | "A4")}
                className="mt-1 h-9 rounded border border-border px-2 text-sm"
              >
                <option value="THERMAL">Termalna (10x15cm)</option>
                <option value="A4">A4</option>
              </select>
            </div>
            {labelFormat === "A4" && (
              <div>
                <label className="block text-xs font-medium text-slate-500">Pozicija na A4</label>
                <select
                  value={labelPosition}
                  onChange={(e) => setLabelPosition(e.target.value)}
                  className="mt-1 h-9 rounded border border-border px-2 text-sm"
                >
                  <option value="1">Pozicija 1</option>
                  <option value="2">Pozicija 2</option>
                  <option value="3">Pozicija 3</option>
                  <option value="4">Pozicija 4</option>
                </select>
              </div>
            )}
            <Button variant="secondary" onClick={printLabel}>
              Štampaj nalepnicu
            </Button>
          </div>
          {printError && <p className="text-xs text-red-700">{printError}</p>}
        </div>
      ) : usableCouriers.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          Nijedna kurirska služba nije podešena. Podesi je u Podešavanja -&gt; Kurirske službe.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap items-end gap-2 text-sm">
          <div>
            <label className="block text-xs font-medium text-slate-500">Kurir</label>
            <select
              value={selectedCourier}
              onChange={(e) => setCourier(e.target.value)}
              className="mt-1 h-9 rounded border border-border px-2 text-sm"
            >
              <option value="">Izaberi...</option>
              {usableCouriers.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500">Težina (kg)</label>
            <input
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              className="mt-1 h-9 w-20 rounded border border-border px-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500">Otkup (RSD)</label>
            <input
              value={codAmount}
              onChange={(e) => setCodAmount(e.target.value)}
              placeholder="0"
              className="mt-1 h-9 w-24 rounded border border-border px-2 text-sm"
            />
          </div>
          <Button disabled={!selectedCourier || dispatch.isPending} onClick={() => dispatch.mutate()}>
            Pošalji kuriru
          </Button>
        </div>
      )}
      {dispatch.isError && (
        <p className="mt-2 text-xs text-red-700">
          {dispatch.error instanceof Error ? dispatch.error.message : "Slanje kuriru nije uspelo."}
        </p>
      )}
    </div>
  );
}

function IssueOrderModal({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [serials, setSerials] = useState<Record<string, string>>({});

  const { data: order, isLoading } = useQuery({
    queryKey: ["warehouse-issue-order", orderId],
    queryFn: () => api.getWarehouseIssueOrder(orderId),
  });

  // Popuni količinu ODMAH sa preostalim iznosom (najčešći slučaj - izdaje
  // se sve odjednom). Ranije je preostala količina bila samo placeholder
  // (siva pomoćna vrednost, ne prava vrednost polja) - klik na "Izdaj
  // robu" bez ručnog kucanja slao je praznu listu i ništa se nije menjalo,
  // bez ikakve greške.
  useEffect(() => {
    if (!order) return;
    setQuantities((prev) => {
      const next = { ...prev };
      for (const item of order.items) {
        if (next[item.id] === undefined) {
          const remaining = Number(item.requestedQuantity) - Number(item.issuedQuantity);
          next[item.id] = remaining > 0 ? remaining.toString() : "0";
        }
      }
      return next;
    });
  }, [order]);

  const issue = useMutation({
    mutationFn: () =>
      api.issueWarehouseOrderItems(
        orderId,
        Object.entries(quantities)
          .filter(([, qty]) => qty && Number(qty) > 0)
          .map(([itemId, qty]) => ({
            itemId,
            issuedQuantity: qty,
            issuedSerialNumbers: serials[itemId] || undefined,
          })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouse-issue-order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["warehouse-issue-orders"] });
      setQuantities({});
      setSerials({});
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Nalog magacinu</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        {isLoading || !order ? (
          <p className="p-6 text-sm text-slate-500">Učitavanje...</p>
        ) : (
          <div className="space-y-4 px-6 py-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-400">Kupac</p>
                <p className="font-medium text-slate-800">{order.customerName ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Faktura</p>
                <p className="font-medium text-slate-800">{order.salesDocumentNumber ?? "—"}</p>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Artikal</th>
                  <th>Traženo</th>
                  <th>Izdato</th>
                  <th>Izdaj sada</th>
                  <th>Serijski brojevi</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => {
                  const remaining = Number(item.requestedQuantity) - Number(item.issuedQuantity);
                  return (
                    <tr key={item.id}>
                      <td className="font-medium text-slate-800">{item.name}</td>
                      <td>{item.requestedQuantity}</td>
                      <td>{item.issuedQuantity}</td>
                      <td className="w-24">
                        {remaining > 0 ? (
                          <input
                            value={quantities[item.id] ?? remaining.toString()}
                            onChange={(e) =>
                              setQuantities((prev) => ({ ...prev, [item.id]: e.target.value }))
                            }
                            className="h-8 w-full rounded border border-border px-2 text-right text-sm focus:border-brand focus:outline-none"
                          />
                        ) : (
                          <span className="text-xs text-emerald-600">Kompletno</span>
                        )}
                      </td>
                      <td className="w-40">
                        {remaining > 0 && (
                          <input
                            value={serials[item.id] ?? ""}
                            onChange={(e) => setSerials((prev) => ({ ...prev, [item.id]: e.target.value }))}
                            placeholder="SN1, SN2..."
                            className="h-8 w-full rounded border border-border px-2 text-xs focus:border-brand focus:outline-none"
                          />
                        )}
                        {item.issuedSerialNumbers && (
                          <p className="mt-0.5 text-xs text-slate-400">Već izdato: {item.issuedSerialNumbers}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {order.status !== "COMPLETED" && (
              <div className="flex flex-col items-end gap-2">
                {issue.isError && (
                  <p className="w-full rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                    {issue.error instanceof Error ? issue.error.message : "Izdavanje nije uspelo."}
                  </p>
                )}
                <Button onClick={() => issue.mutate()} disabled={issue.isPending}>
                  Izdaj robu
                </Button>
              </div>
            )}

            <CourierDispatchSection orderId={orderId} orderStatus={order.status} />
          </div>
        )}
      </div>
    </div>
  );
}

export function WarehouseOrdersTab() {
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { data: orders, isLoading } = useQuery({
    queryKey: ["warehouse-issue-orders"],
    queryFn: () => api.listWarehouseIssueOrders(),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Nalog magacinu se pravi automatski kada otvorite fakturu koja ima broj, ako firma koristi
        magacinsko poslovanje.
      </p>
      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !orders || orders.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={ClipboardList}
              title="Nema naloga magacinu"
              description="Pojaviće se ovde čim se kreira sa strane neke fakture."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Faktura</th>
                <th>Kupac</th>
                <th>Magacin</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewingId(o.id)}>
                  <td className="font-medium text-slate-800">{o.salesDocumentNumber ?? "—"}</td>
                  <td className="text-slate-600">{o.customerName ?? "—"}</td>
                  <td className="text-slate-500">{o.warehouseName}</td>
                  <td>
                    <StatusBadge label={STATUS_LABEL[o.status] ?? o.status} tone={STATUS_TONE[o.status] ?? "neutral"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {viewingId && <IssueOrderModal orderId={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
}
